import { Router } from "@angular/router";
import { AuthUser } from "./../shared/auth.service";
import { DomainService } from "./../shared/domain.service";
import { UtilFns } from "./util-fns";
import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { DbQueryService } from "./db-query.service";
import { UnitOfWork } from "./unit-of-work";
import { AgFns, GridOptionsExt } from "./ag-fns";
import { DbSaveService } from "./db-save.service";
import { provideParent } from "./provide-parent";
import { DialogService } from "primeng/dynamicdialog";
import { DialogWrapperService } from "./dialog-wrapper.service";
import { ToastrService } from "ngx-toastr";
import { BusyService } from "./busy.service";

// The wrapBusy decorator - applied to a function it wraps the entire execution of the function in a busy indicator.
// wrapBusy can be nested safely.

export function wrapBusy(target, key, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = async function (...args) {
    // wrapBusy might be on the component or the component's 'parent'.
    const wrapBusy =
      (this.wrapBusy && this.wrapBusy.bind(this)) ??
      (this.parent?.wrapBusy && this.parent.wrapBusy.bind(parent));
    if (wrapBusy) {
      return await wrapBusy(async () => await originalMethod.apply(this, args));
    } else {
      return await originalMethod.apply(this, args);
    }
  };
}

@Component({
  template: "<div></div>",
  providers: [provideParent(this)],
  standalone: false,
})
export abstract class DomainBaseComponent implements OnInit, OnDestroy {
  authUser: AuthUser;

  self: DomainBaseComponent;
  UtilFns = UtilFns; // needed in order to access UtilFns from within html
  AgFns = AgFns;
  private _isPageReady: boolean;

  public pngDialogService: DialogService;
  public dialogService: DialogWrapperService;

  protected dbQueryService: DbQueryService;
  protected dbSaveService: DbSaveService;
  protected toastr: ToastrService;

  protected router: Router;
  protected onDestroy = new Subject<void>();
  public uow: UnitOfWork;
  protected isCancelling = false;

  public busyService: BusyService;

  public busyPromise: Promise<any> | undefined;

  user: AuthUser;

  constructor(protected domainService: DomainService) {
    this.authUser = domainService.authService.getUser();

    this.dbQueryService = domainService.dbQueryService;
    this.dbSaveService = domainService.dbSaveService;
    this.uow = domainService.uow;
    this.router = domainService.router;
    this.pngDialogService = domainService.pngDialogService;
    this.dialogService = domainService.dialogService;
    this.toastr = domainService.toastService;
    this.busyService = domainService.busyService;
    this.self = this;
    this.isPageReady = false;
  }

  ngOnInit(): void {
    this.user = this.domainService.authService.getUser();
  }

  ngOnDestroy(): void {
    // used to unsubscribe from subscriptions.
    this.onDestroy.next();
  }

  get isPageReady() {
    return this._isPageReady;
  }
  set isPageReady(val: boolean) {
    this._isPageReady = val;
  }

  getPageIcon() {
    const route = this["route"];
    const pageIcon = route?.snapshot.routeConfig.data?.icon;
    return pageIcon;
  }

  /** get named param from the end of the current route */
  getRouteParam(name: string) {
    let rt = this.router.routerState.snapshot.root;
    while (rt.firstChild) {
      rt = rt.firstChild;
    }
    return rt.params[name];
  }

  /** get named param from the first occurance in the current route */
  getFirstParam(name: string) {
    let rt = this.router.routerState.snapshot.root;
    while (!rt.params[name] && rt.firstChild) {
      rt = rt.firstChild;
    }
    return rt.params[name];
  }

  dbLookup(lookupTypeName: string, lookupId: any) {
    return this.dbQueryService.lookup(lookupTypeName, lookupId);
  }

  getRouteSnapshot() {
    let ss = this.router.routerState.snapshot;
    return ss;
  }

  noop() {
    // do nothing
  }

  goBack() {
    this.domainService.location.back();
  }

  hasChanges(): boolean {
    return this.dbSaveService.hasChanges();
  }

  canSave(): boolean {
    return !this.busyService.isBusy() && this.hasChanges();
  }

  canUndo(): boolean {
    return !this.busyService.isBusy() && this.hasChanges();
  }

  /** Undo changes and navigate back to parent page */
  async onCancel() {
    if (!(await this.confirmCancelIfNeeded())) {
      return;
    }
    this.isCancelling = true;
    if (this.canDeactivate) {
      if (await this.canDeactivate()) {
        await this.goBack();
      }
    }
    this.isCancelling = false;
  }

  public async canDeactivate() {
    if (this.busyPromise) {
      await this.busyPromise;
    }

    // TODO: Consider removing dbSaveService.busyPromise and just using busyService.busyPromise
    if (this.dbSaveService.busyPromise) {
      await this.dbSaveService.busyPromise;
    }

    // if (this.busyService.busyPromise) {
    //   await this.busyService.busyPromise;
    // }

    if (this.isCancelling) {
      return true;
    }
    return await this.confirmCancelIfNeeded();
  }

  async confirmCancelIfNeeded() {
    if (this.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog('Cancel Confirmation', 'You have unsaved changes.  Do you still want to cancel?');
      if (ok) {
        this.dbSaveService.rejectChanges();
      }
      return ok;
    }
    return true;
  }

  /** Stop window closing if there are unsaved changes - cannot be async */
  @HostListener("window:beforeunload")
  public canUnload() {
    // will be overloaded later
    return true;
  }

  // TODO: Later we should consider making this abstract
  public deactivate() {
    this.isPageReady = false;
    // should be overriden
  }

  public isBusy() {
    // DON'T add this.busyService.isBusy() here, it will cause ExpressionChangedAfterItHasBeenCheckedError in some cases.
    return this.busyPromise != null || this.dbSaveService.isBusy();
  }

  async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {
    if (this.busyPromise) {
      return await (typeof op === "function" ? op() : op);
    }

    try {
      this.busyPromise = typeof op === "function" ? op() : op;
      return await this.busyPromise;
    } finally {
      this.busyPromise = undefined;
    }
  }

  private _locationGridOptionsExt: GridOptionsExt;
  public captureLocation(
    gridOptionsExt: GridOptionsExt,
    keyName: string = null
  ) {
    this._locationGridOptionsExt = gridOptionsExt;
    return this.domainService.navigationStateService.captureLocation(
      this,
      gridOptionsExt,
      keyName
    );
  }

  public replaceLocation(
    key: any = null,
    otherQueryParams: { [key: string]: any } = {}
  ) {
    UtilFns.assertNonNull(
      this._locationGridOptionsExt,
      "captureLocation must be called before replaceLocation"
    );
    if (!this._applyLocationHasRun) return;
    this.domainService.navigationStateService.replaceLocation(
      this,
      this._locationGridOptionsExt,
      key,
      otherQueryParams
    );
  }

  private _applyLocationHasRun = false;
  public async applyLocation() {
    UtilFns.assertNonNull(
      this._locationGridOptionsExt,
      "captureLocation must be called before applyLocation"
    );

    await AgFns.applyGridRouteParams(this._locationGridOptionsExt);
    this._applyLocationHasRun = true;
  }

  // OLDER VERSION - NOT as performant as the above

  // private _busyCount = 0;
  // public isBusy() {
  //   return this._busyCount > 0 || this.dbSaveService.isBusy();
  //   // TODO: would like to do this but the AuthHttpInterceptor calls setBusy during the
  //   // opening of a dialog during any query or similar http operation
  //   // which in turn causes an ExpressionChangedAfterItHasBeenCheckedError for any isBusy
  //   // ngIf's on the parent component.  Need to figure out how to avoid this.
  //   // return this._isBusy || this.dbSaveService.isSaving || this.busyService.isBusyValue;
  // }

  // // NOTE: this is different from the busyService.busy method in that it sets the isBusy flag on this component only
  // // This means that you can wrapBusy on a parent component method that calls a dialog and not have to clear
  // // the busy signal before the dialog open.  Each dialog has its own isBusy and wrapBusy method that only operates
  // // locally
  // // doesn't handle putting up wait indicator... this is the resp of the component itself looking at the
  // // isBusy property.

  // // also performs a guardDeactivate
  // async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {
  //   const promise = typeof op === 'function' ? op() : op;

  //   try {
  //     if (this._busyCount === 0) {
  //       this.pendingOp = promise;
  //     }
  //     this._busyCount = this._busyCount + 1;
  //     return await promise;
  //   } finally {
  //     this._busyCount = this._busyCount - 1;
  //     if (this._busyCount === 0) {
  //       this.pendingOp = undefined;
  //     }
  //   }
  // }
}
